const CSS_FOCUS = 'focus';
const CSS_ACTIVE = 'active';
const CSS_INVALID = 'invalid';

export class InputController {
    constructor(root, input, params = {}) {
        this._root = root;
        this._params = params;
        this._input = input;
        this._placeholder = root.querySelector('span');

        // Force this binding for `addEventListener` listeners
        this._onBlur = this._onBlur.bind(this);
        this._onFocus = this._onFocus.bind(this);
        this._onInput = this._onInput.bind(this);
        this._onClickPlaceholder = this._onClickPlaceholder.bind(this);
    }

    initialize() {
        this._toggleComponentActiveState();

        // Force focus if it should be active by default.
        if (this._activeByDefault()) {
            this._focus();
        }

        this._addListeners();
    }

    destroy() {
        this._removeListeners();

        this._root = null;
        this._params = null;
        this._input = null;
        this._placeholder = null;
    }

    _addListeners() {
        this._input.addEventListener('focus', this._onFocus);
        this._input.addEventListener('blur', this._onBlur);
        this._input.addEventListener('input', this._onInput);

        if (this._placeholder) {
            this._placeholder.addEventListener('click', this._onClickPlaceholder);
        }
    }

    _removeListeners() {
        this._input.removeEventListener('focus', this._onFocus);
        this._input.removeEventListener('blur', this._onBlur);
        this._input.removeEventListener('input', this._onInput);

        if (this._placeholder) {
            this._placeholder.removeEventListener('click', this._onClickPlaceholder);
        }
    }

    _onClickPlaceholder() {
        // Force focus on input.
        // In some weird cases, the click does not trigger a focus on the input, so it
        // never becomes active and remains unusable.
        this._input.focus();
    }

    _onBlur() {
        this._toggleComponentActiveState();
        this._removeFocus();
    }

    _onFocus() {
        this._setActive();
        this._setFocus();
    }

    _onInput() {
        // Remove invalidity visual effect.
        this._removeInvalidState();

        // Do not forget the active state.
        if (this._isFilled()) {
            this._setActive();
        }
    }

    _toggleComponentActiveState() {
        this._toggleActive(this._isFilled() || this._activeByDefault());
    }

    _focus() {
        this._input.focus();
    }

    _value() {
        return this._input.value;
    }

    _isFilled() {
        return !!this._value();
    }

    _setActive() {
        this._toggleActive(true);
    }

    _setInactive() {
        this._toggleActive(false);
    }

    _setFocus() {
        this._root.classList.add(CSS_FOCUS);
    }

    _removeFocus() {
        this._root.classList.remove(CSS_FOCUS);
    }

    _toggleActive(isActive) {
        this._root.classList.toggle(CSS_ACTIVE, isActive);
    }

    _isActive() {
        return this._root.classList.contains(CSS_ACTIVE);
    }

    _isInvalid() {
        return this._root.classList.contains(CSS_INVALID);
    }

    _removeInvalidState() {
        this._toggleValidState(true);
    }

    _addInvalidState() {
        this._toggleValidState(false);
    }

    _toggleValidState(isValid) {
        this._root.classList.toggle(CSS_INVALID, !isValid);
    }

    _activeByDefault() {
        return this._params.active || false;
    }

    _isValid() {
        return this._input.checkValidity();
    }
}
